import React from "react";
import { LoadingRow } from "@/components/LoadingState";
import { Edit, FileText, Trash2, ExternalLink, Check, Clock, X as XIcon, Eye, EyeOff } from "lucide-react";
import { formatNum, fmtDate } from "@/lib/api";
import { PrimMonthBadge } from "./PrimMonth";
import { SortProvider, STh, useColumnSort } from "@/components/SortTh";
import { getPozisyonColor, isCallCenterPozisyon } from "@/lib/pozisyonColors";

function Th({ children, right, center, k }) {
  const align = right ? "text-right" : center ? "text-center" : "text-left";
  const cls = `px-3 py-3 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap ${align}`;
  if (k) return <STh k={k} className={cls} testid={`prim-sort-${k}`}>{children}</STh>;
  return (
    <th className={cls}>
      {children}
    </th>
  );
}

const ccIdxOf = (r) => (r.giden_pozisyonlari || []).map((p, i) => (/call ?center|çağrı|cagri/i.test(p || "") ? i : -1)).filter((i) => i >= 0);
const PRIM_GETTERS = {
  durum: (r) => (r.onaylandi ? 1 : 0),
  giden1: (r) => (r.giden_isimleri || [])[0], giden2: (r) => (r.giden_isimleri || [])[1],
  giden3: (r) => (r.giden_isimleri || [])[2], giden4: (r) => (r.giden_isimleri || [])[3],
  cc: (r) => ccIdxOf(r).map((i) => (r.giden_isimleri || [])[i]).join(", "),
  cc_primi: (r) => ccIdxOf(r).reduce((s, i) => s + Number((r.kisi_basi_detay || {})[(r.giden_kisiler || [])[i]] || 0), 0) || null,
};

function Td({ children, right, center, bold, mono, tone }) {
  const align = right ? "text-right" : center ? "text-center" : "text-left";
  const toneCls = tone === "emerald" ? "text-emerald-700" : tone === "indigo" ? "text-indigo-700" : "text-slate-800";
  const w = bold ? "font-semibold" : "";
  const m = mono ? "font-mono-tab" : "";
  return <td className={`px-3 py-2.5 whitespace-nowrap ${align} ${w} ${m} ${toneCls}`}>{children}</td>;
}

export default function PrimTable({
  rows,
  totals,
  loading,
  canSeePrimRate,
  isPersonel,
  isAdmin,
  onEdit,
  onDelete,
  onPdf,
  onApprove,
  onReject,
  onToggleVarlik,
  onSetVisibility,
  selectedIds,
  onToggleRow,
}) {
  const showSelect = isAdmin && !!onToggleRow;
  const colSort = useColumnSort(rows, [], PRIM_GETTERS);
  return (
    <div className="sticky-table hidden md:block">
      <SortProvider value={colSort}>
      <table className="w-full text-sm" data-testid="prim-table">
        <thead>
          <tr className="bg-slate-900 text-slate-100">
            {showSelect && <Th center><span className="sr-only">Seç</span></Th>}
            <Th k="tarih">Tarih</Th>
            <Th center k="durum">Durum</Th>
            <Th k="icra_foy">İcra Föy</Th>
            <Th k="icra_esas">İcra Esas</Th>
            <Th k="dosya_adi">Dosya Adı</Th>
            <Th k="alacakli_adi">Müvekkil Adı</Th>
            <Th center k="varlik_sirketi">Varlık</Th>
            {canSeePrimRate && <Th right k="oran">Oran</Th>}
            <Th right k="vekalet_ucreti">Vekalet Ücreti</Th>
            <Th right k="tahsilat_miktari">Tahsilat Miktarı</Th>
            {canSeePrimRate && <Th right k="odenecek_toplam">Ödenecek Toplam</Th>}
            <Th k="giden1">Giden Kişi 1</Th>
            <Th k="giden2">Giden Kişi 2</Th>
            <Th k="giden3">Giden Kişi 3</Th>
            <Th k="giden4">Giden Kişi 4</Th>
            {canSeePrimRate && <Th right k="kisi_basi_prim">Kişi Başı</Th>}
            {!isPersonel && <Th k="cc">Call Center</Th>}
            {canSeePrimRate && <Th right k="cc_primi">CC Primi</Th>}
            {!isPersonel && <Th k="arastiran_isim">Araştıran</Th>}
            {canSeePrimRate && <Th right k="arastirma_primi">Araşt. Primi</Th>}
            <Th></Th>
          </tr>
        </thead>
        <tbody>
          {loading && <LoadingRow colSpan={showSelect ? 22 : 21} text="Primler yükleniyor..." />}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={showSelect ? 22 : 21} className="text-center py-10 text-slate-400">Prim kaydı bulunamadı. Sağ üstten ekleyebilirsiniz.</td></tr>
          )}
          {colSort.sorted.map((r) => {
            const g = r.giden_isimleri || [];
            const checked = selectedIds?.has?.(r.id);
            return (
              <React.Fragment key={r.id}>
              <tr data-testid={`prim-row-${r.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${r.onaylandi === false ? "bg-amber-50/40" : ""} ${checked ? "ring-1 ring-indigo-300" : ""}`}>
                {showSelect && (
                  <td className="px-3 py-2.5 text-center">
                    <input
                      data-testid={`prim-row-check-${r.id}`}
                      type="checkbox"
                      checked={!!checked}
                      onChange={() => onToggleRow(r.id)}
                      className="w-4 h-4 accent-indigo-600 cursor-pointer"
                    />
                  </td>
                )}
                <Td mono>{fmtDate(r.tarih)}<PrimMonthBadge r={r} /></Td>
                <Td center>
                  {r.onaylandi ? (
                    <span data-testid={`prim-status-approved-${r.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200">
                      <Check size={10} /> Onaylı
                    </span>
                  ) : (
                    <span data-testid={`prim-status-pending-${r.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
                      <Clock size={10} /> Bekliyor
                    </span>
                  )}
                  {r.onay_override && (
                    <span
                      data-testid={`prim-override-badge-${r.id}`}
                      className="inline-flex items-center gap-0.5 ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-red-100 text-red-800 border border-red-300 align-middle"
                      title={`Admin OVERRIDE ile onaylandı · ${r.onay_override_blockers?.length || 0} mükerrer föy bypass edildi · onaylayan: ${r.onaylayan_email || "?"}`}
                    >
                      ⚠ OVERRIDE
                    </span>
                  )}
                  {isAdmin && r.onaylandi && onSetVisibility && (
                    <div className="inline-flex items-center gap-0.5 ml-1 align-middle">
                      <button
                        data-testid={`visibility-visible-${r.id}`}
                        onClick={() => onSetVisibility(r, "forced_visible")}
                        title="Personel kalıcı görsün (7 günden sonra da)"
                        className={`w-5 h-5 rounded grid place-items-center transition-colors ${
                          r.personel_visibility === "forced_visible"
                            ? "bg-emerald-500 text-white"
                            : "text-emerald-600 hover:bg-emerald-50"
                        }`}
                      ><Eye size={11} /></button>
                      <button
                        data-testid={`visibility-hidden-${r.id}`}
                        onClick={() => onSetVisibility(r, "forced_hidden")}
                        title="Personel'den hemen gizle"
                        className={`w-5 h-5 rounded grid place-items-center transition-colors ${
                          r.personel_visibility === "forced_hidden"
                            ? "bg-rose-500 text-white"
                            : "text-rose-600 hover:bg-rose-50"
                        }`}
                      ><EyeOff size={11} /></button>
                      {r.personel_visibility && (
                        <button
                          data-testid={`visibility-default-${r.id}`}
                          onClick={() => onSetVisibility(r, "default")}
                          title="Varsayılan 7 gün kuralına döndür"
                          className="w-5 h-5 rounded grid place-items-center text-slate-500 hover:bg-slate-100 text-[9px] font-bold"
                        >↺</button>
                      )}
                    </div>
                  )}
                </Td>
                <Td mono>{r.icra_foy}</Td>
                <Td mono>
                  <div className="flex items-center gap-1">
                    {r.icra_esas}
                    {r.uyap_url && (
                      <a
                        href={r.uyap_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-blue-50 text-blue-700"
                        title="UYAP'ta Aç"
                        data-testid={`uyap-link-${r.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                </Td>
                <Td bold>{r.dosya_adi}</Td>
                <Td>
                  <div className="flex flex-col gap-0.5">
                    <span>{r.alacakli_adi}</span>
                    {Array.isArray(r.tags) && r.tags.length > 0 && (
                      <div className="flex flex-wrap gap-0.5" data-testid={`prim-row-tags-${r.id}`}>
                        {r.tags.slice(0, 3).map((t) => (
                          <span key={t} className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0 rounded-full bg-sky-100 text-sky-700 border border-sky-200">
                            # {t}
                          </span>
                        ))}
                        {r.tags.length > 3 && (
                          <span className="text-[9px] text-slate-400">+{r.tags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                </Td>
                <Td center>
                  {isAdmin && onToggleVarlik ? (
                    <button
                      data-testid={`varlik-toggle-${r.id}`}
                      onClick={() => onToggleVarlik(r)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border transition-colors ${
                        r.varlik_sirketi
                          ? "bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-200"
                          : "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200"
                      }`}
                      title={r.varlik_sirketi ? "Varlık şirketi: EVET (tıkla, HAYIR yap)" : "Varlık şirketi: HAYIR (tıkla, EVET yap)"}
                    >
                      {r.varlik_sirketi ? "Varlık" : "—"}
                    </button>
                  ) : (
                    r.varlik_sirketi ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">Varlık</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">—</span>
                    )
                  )}
                </Td>
                {canSeePrimRate && <Td right mono>%{formatNum(r.oran)}</Td>}
                <Td right mono>{formatNum(r.vekalet_ucreti)}</Td>
                <Td right mono>{formatNum(r.tahsilat_miktari)}</Td>
                {canSeePrimRate && <Td right mono bold tone="emerald">{formatNum(r.odenecek_toplam)}</Td>}
                <Td>{g[0] || "—"}</Td>
                <Td>{g[1] || "—"}</Td>
                <Td>{g[2] || "—"}</Td>
                <Td>{g[3] || "—"}</Td>
                {canSeePrimRate && (
                  <Td right mono bold tone="emerald">
                    {(() => {
                      const detay = r.kisi_basi_detay || {};
                      const gids = r.giden_kisiler || [];
                      const pozs = r.giden_pozisyonlari || [];
                      const names = r.giden_isimleri || [];
                      // Group gidens by pozisyon (fall back to "—" when empty)
                      const groups = new Map();
                      gids.forEach((gid, i) => {
                        const poz = (pozs[i] || "—").trim() || "—";
                        const amt = detay[gid];
                        if (amt === undefined) return;
                        if (!groups.has(poz)) groups.set(poz, { count: 0, amt, members: [] });
                        const g = groups.get(poz);
                        g.count += 1;
                        g.amt = amt; // same per-position; CC & non-CC each share same within pozisyon
                        g.members.push(names[i] || "—");
                      });
                      const posKeys = Array.from(groups.keys());
                      const hasMultiPoz = posKeys.length >= 2;
                      // Build tooltip with CC marker
                      const tipLines = posKeys.map((poz) => {
                        const g = groups.get(poz);
                        const isCC = /call ?center|çağrı|cagri/i.test(poz);
                        const label = isCC ? `${poz} (CC %7)` : poz;
                        return `• ${label} × ${g.count}: ${new Intl.NumberFormat("tr-TR",{style:"currency",currency:"TRY"}).format(g.amt||0)} / kişi`;
                      });
                      // Multi-position: show per-pozisyon breakdown directly in cell
                      if (hasMultiPoz && Object.keys(detay).length > 0) {
                        return (
                          <div
                            data-testid={`prim-kisibasi-multi-${r.id}`}
                            className="flex flex-col gap-0.5 items-end leading-tight"
                            title={tipLines.join("\n")}
                          >
                            {posKeys.map((poz) => {
                              const g = groups.get(poz);
                              const isCC = isCallCenterPozisyon(poz);
                              const col = getPozisyonColor(poz);
                              const short = poz.length > 14 ? poz.slice(0, 13) + "…" : poz;
                              return (
                                <div
                                  key={poz}
                                  className="inline-flex items-center gap-1 text-[11px]"
                                  data-testid={`prim-kisibasi-poz-${r.id}-${poz}`}
                                >
                                  {isCC ? (
                                    <span
                                      className="inline-flex items-center justify-center w-3 h-3 rounded-full text-[7px] font-bold leading-none"
                                      style={{ background: col.dotBg, color: col.dotText }}
                                      title="Call Center %7"
                                    >%</span>
                                  ) : (
                                    <span
                                      className="inline-flex w-2 h-2 rounded-full"
                                      style={{ background: col.dotBg }}
                                      aria-hidden="true"
                                    />
                                  )}
                                  <span className="font-medium normal-case" style={{ color: col.chipText }}>{short}:</span>
                                  <span className="font-mono-tab font-semibold" style={{ color: col.dotBg }}>{formatNum(g.amt)}</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }
                      // Single-position (legacy): show average value with CC indicator if applicable
                      if (r.has_call_center && r.kisi_basi_detay) {
                        return (
                          <span
                            className="relative inline-flex items-center gap-0.5 cursor-help"
                            title={tipLines.join("\n")}
                            data-testid={`prim-cc-indicator-${r.id}`}
                          >
                            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-amber-500 text-white text-[8px] font-bold leading-none">%</span>
                            {formatNum(r.kisi_basi_prim)}
                          </span>
                        );
                      }
                      return formatNum(r.kisi_basi_prim);
                    })()}
                  </Td>
                )}
                {/* Call Center column: dedicated name/badge (parallels Araştıran) */}
                {!isPersonel && (() => {
                  const gids = r.giden_kisiler || [];
                  const pozs = r.giden_pozisyonlari || [];
                  const names = r.giden_isimleri || [];
                  const ccIdx = pozs
                    .map((p, i) => (/call ?center|çağrı|cagri/i.test(p || "") ? i : -1))
                    .filter((i) => i >= 0);
                  if (ccIdx.length === 0) {
                    return <Td><span className="text-slate-400 text-xs">—</span></Td>;
                  }
                  const ccNames = ccIdx.map((i) => names[i] || "—");
                  return (
                    <Td>
                      <div className="flex flex-wrap items-center gap-1" data-testid={`prim-cc-cell-${r.id}`}>
                        {ccNames.map((n, k) => (
                          <span
                            key={`${n}-${k}`}
                            className="inline-flex items-center gap-0.5 bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded text-[10px] font-semibold"
                            title={`Call Center · ${n}`}
                          >
                            <span className="inline-flex items-center justify-center w-3 h-3 rounded-full bg-amber-500 text-white text-[7px] font-bold leading-none">%</span>
                            {n}
                          </span>
                        ))}
                      </div>
                    </Td>
                  );
                })()}
                {canSeePrimRate && (() => {
                  const detay = r.kisi_basi_detay || {};
                  const gids = r.giden_kisiler || [];
                  const pozs = r.giden_pozisyonlari || [];
                  const ccIdx = pozs
                    .map((p, i) => (/call ?center|çağrı|cagri/i.test(p || "") ? i : -1))
                    .filter((i) => i >= 0);
                  if (ccIdx.length === 0) {
                    return <Td right mono><span className="text-slate-400">—</span></Td>;
                  }
                  // Each CC personel gets the same per-person amount; sum = ccIdx.length × amount
                  const perPerson = detay[gids[ccIdx[0]]] || 0;
                  const total = perPerson * ccIdx.length;
                  const title = `${ccIdx.length} CC personel × ${new Intl.NumberFormat("tr-TR",{style:"currency",currency:"TRY"}).format(perPerson)} = ${new Intl.NumberFormat("tr-TR",{style:"currency",currency:"TRY"}).format(total)}`;
                  return (
                    <Td right mono bold tone="emerald">
                      <span data-testid={`prim-cc-amount-${r.id}`} className="inline-flex items-center gap-1 cursor-help" title={title}>
                        {formatNum(perPerson)}
                        {ccIdx.length > 1 && (
                          <span className="inline-flex items-center bg-amber-100 text-amber-700 px-1 rounded text-[9px] font-bold border border-amber-200">×{ccIdx.length}</span>
                        )}
                      </span>
                    </Td>
                  );
                })()}
                {!isPersonel && <Td>{r.arastiran_isim || "—"}</Td>}
                {canSeePrimRate && <Td right mono bold tone="indigo">{formatNum(r.arastirma_primi)}</Td>}
                <Td right>
                  {isAdmin && !r.onaylandi && onApprove && (
                    <>
                      <button
                        data-testid={`approve-prim-${r.id}`}
                        onClick={() => onApprove(r)}
                        className="w-7 h-7 rounded hover:bg-emerald-50 text-emerald-600 inline-flex items-center justify-center"
                        title="Onayla"
                      ><Check size={14} /></button>
                      <button
                        data-testid={`reject-prim-${r.id}`}
                        onClick={() => onReject(r)}
                        className="w-7 h-7 rounded hover:bg-rose-50 text-rose-600 inline-flex items-center justify-center"
                        title="Reddet (kayıt silinir)"
                      ><XIcon size={14} /></button>
                    </>
                  )}
                  {!isPersonel && (
                    <button
                      data-testid={`pdf-prim-${r.id}`}
                      onClick={() => onPdf(r)}
                      className="w-7 h-7 rounded hover:bg-rose-50 text-rose-600 inline-flex items-center justify-center"
                      title="PDF Makbuz"
                    ><FileText size={13} /></button>
                  )}
                  <button
                    data-testid={`edit-prim-${r.id}`}
                    onClick={() => onEdit(r)}
                    className={`w-7 h-7 rounded hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center ${
                      !isAdmin && r.onaylandi ? "opacity-30 cursor-not-allowed" : ""
                    }`}
                    disabled={!isAdmin && r.onaylandi}
                    title={!isAdmin && r.onaylandi ? "Onaylanmış prim sadece admin tarafından düzenlenebilir" : "Düzenle"}
                  ><Edit size={13} /></button>
                  {(isAdmin || !r.onaylandi) && (
                    <button
                      data-testid={`delete-prim-${r.id}`}
                      onClick={() => onDelete(r.id)}
                      className="w-7 h-7 rounded hover:bg-rose-50 text-rose-500 inline-flex items-center justify-center"
                      title={isAdmin ? "Sil" : "Onay bekleyen primi sil"}
                    ><Trash2 size={13} /></button>
                  )}
                </Td>
              </tr>
              {/* Per-row position totals strip — only when multiple positions exist */}
              {canSeePrimRate && (() => {
                const detay = r.kisi_basi_detay || {};
                const gids = r.giden_kisiler || [];
                const pozs = r.giden_pozisyonlari || [];
                if (gids.length === 0 || Object.keys(detay).length === 0) return null;
                const groups = new Map();
                gids.forEach((gid, i) => {
                  const poz = (pozs[i] || "—").trim() || "—";
                  const amt = detay[gid];
                  if (amt === undefined) return;
                  if (!groups.has(poz)) groups.set(poz, { count: 0, amt, total: 0 });
                  const g = groups.get(poz);
                  g.count += 1;
                  g.amt = amt;
                  g.total = amt * g.count;
                });
                if (groups.size < 2) return null;
                const colSpan = showSelect ? 22 : 21;
                return (
                  <tr
                    data-testid={`prim-poz-totals-${r.id}`}
                    className="bg-slate-50 border-b border-slate-200"
                  >
                    <td colSpan={colSpan} className="px-3 py-1.5">
                      <div className="flex items-center gap-2 flex-wrap text-[11px]">
                        <span className="text-slate-500 font-semibold uppercase tracking-wider">Pozisyon Toplamı:</span>
                        {Array.from(groups.entries()).map(([poz, g]) => {
                          const isCC = isCallCenterPozisyon(poz);
                          const col = getPozisyonColor(poz);
                          return (
                            <span
                              key={poz}
                              data-testid={`prim-poz-total-chip-${r.id}-${poz}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-semibold"
                              style={{
                                background: col.chipBg,
                                borderColor: col.chipBorder,
                                color: col.chipText,
                              }}
                            >
                              {isCC ? (
                                <span
                                  className="inline-flex items-center justify-center w-3 h-3 rounded-full text-[7px] font-bold leading-none"
                                  style={{ background: col.dotBg, color: col.dotText }}
                                >%</span>
                              ) : (
                                <span
                                  className="inline-flex w-2 h-2 rounded-full"
                                  style={{ background: col.dotBg }}
                                  aria-hidden="true"
                                />
                              )}
                              <span>{poz}</span>
                              <span className="opacity-70">×{g.count}</span>
                              <span className="font-mono-tab" style={{ color: col.dotBg }}>= {formatNum(g.total)}</span>
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })()}
              </React.Fragment>
            );
          })}
        </tbody>
        {rows.length > 0 && canSeePrimRate && (
          <tfoot>
            <tr className="bg-slate-100 font-bold">
              <td colSpan={showSelect ? 9 : 8} className="px-3 py-3">TOPLAM</td>
              <td className="px-3 py-3 text-right font-mono-tab">{formatNum(totals.vekalet)}</td>
              <td className="px-3 py-3 text-right font-mono-tab">{formatNum(totals.tahsilat)}</td>
              <td className="px-3 py-3 text-right font-mono-tab text-emerald-700">{formatNum(totals.odenecek)}</td>
              <td colSpan={4}></td>
              <td className="px-3 py-3 text-right font-mono-tab text-emerald-700" data-testid="prim-total-kisi-basi">{formatNum(totals.kisi_basi)}</td>
              <td></td>
              <td className="px-3 py-3 text-right font-mono-tab text-amber-700" data-testid="prim-total-cc">{formatNum(totals.cc || 0)}</td>
              <td></td>
              <td className="px-3 py-3 text-right font-mono-tab text-indigo-700" data-testid="prim-total-arastirma">{formatNum(totals.arastirma_primi)}</td>
              <td></td>
            </tr>
          </tfoot>
        )}
      </table>
      </SortProvider>
    </div>
  );
}
